import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { BarcodeScanner } from '@/shared/components/scanner/BarcodeScanner';
import { ScanWorkflowDialog } from '@/modules/wms/components/scanner/ScanWorkflowDialog';
import { dispatchWorkflowSteps, sampleDispatchItems } from '@/modules/wms/lib/scanWorkflowConfig';
import { dispatchAPI } from '@/shared/services/api';
import {
  Search,
  Truck,
  Package,
  CheckCircle2,
  Clock,
  MapPin,
  FileText,
  ArrowRight,
  Calendar,
  ScanLine,
  ChevronRight,
  Loader2 } from
'lucide-react';
import { toast } from 'sonner';

export const Expedition = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [shipmentStatuses, setShipmentStatuses] = useState({});
  const [dispatches, setDispatches] = useState([]);
  const [dockStatus, setDockStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ staged: 0, packing: 0, shipped: 0 });

  // Fetch dispatch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dispatchData, statsData, docks] = await Promise.all([
        dispatchAPI.getDispatches(),
        dispatchAPI.getStats(),
        dispatchAPI.getDockStatus().catch(() => [])]
        );
        setDispatches(dispatchData || []);
        setDockStatus(docks || []);
        setStats({
          staged: statsData?.staged || dispatchData?.filter((s) => s.status === 'staged').length || 0,
          packing: statsData?.loading || dispatchData?.filter((s) => s.status === 'loading').length || 0,
          shipped: statsData?.shipped_today || 0
        });
      } catch (error) {
        console.error('Failed to fetch dispatch data:', error);
        toast.error('Failed to load dispatch data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const scheduledPickups = [
  { id: 'VEH-001', carrier: 'FedEx Express', time: '14:00', dock: 'DOCK-A1', pallets: 2 },
  { id: 'VEH-002', carrier: 'UPS Ground', time: '16:00', dock: 'DOCK-B2', pallets: 1 }];


  // Handle quick scan
  const handleQuickScan = (result) => {
    if (result.valid) {
      if (result.type === 'INTERNAL') {
        // Check if it's a vehicle scan
        if (result.raw.startsWith('VEH-') || result.raw.includes('TRUCK')) {
          const pickup = scheduledPickups[0];
          setSelectedVehicle(pickup);
          setShowWorkflowDialog(true);
          toast.success('Vehicle Identified', {
            description: `Starting loading for ${pickup.carrier}`
          });
        } else if (result.raw.startsWith('PLT-') || result.raw.startsWith('CTN-')) {
          toast.success('Pallet/Carton Scanned', {
            description: `${result.raw} ready for loading`
          });
        } else {
          toast.info('Scan Recognized', {
            description: `${result.type}: ${result.raw}`
          });
        }
      }
    }
  };

  // Start dispatch workflow
  const handleStartDispatch = (pickup) => {
    setSelectedVehicle(pickup);
    setShowWorkflowDialog(true);
  };

  // Handle workflow completion
  const handleWorkflowComplete = async (data) => {
    if (selectedVehicle) {
      try {
        // Mark all staged items as shipped
        const updates = {};
        dispatches.forEach((s) => {
          if (s.status === 'staged') {
            updates[s.id || s.dispatch_number] = 'shipped';
          }
        });
        setShipmentStatuses((prev) => ({ ...prev, ...updates }));

        // Refresh dispatches
        const dispatchData = await dispatchAPI.getDispatches();
        setDispatches(dispatchData || []);

        toast.success('Dispatch Complete', {
          description: `${data.scannedItems.length} items loaded. BOL generated. Vehicle dispatched.`
        });
      } catch (error) {
        console.error('Failed to complete dispatch:', error);
        toast.error('Failed to complete dispatch');
      }
    }
  };

  // Handle step completion
  const handleStepComplete = (stepIndex, scanResult) => {
    const step = dispatchWorkflowSteps[stepIndex];

    if (step.triggersAction === 'start_load') {
      toast.info('Loading Started', {
        description: 'Scan pallets/cartons to load onto vehicle'
      });
    } else if (step.triggersAction === 'load_item') {
      toast.success('Item Loaded', {
        description: `${scanResult.raw} loaded onto vehicle`
      });
    } else if (step.triggersAction === 'confirm_dispatch') {
      toast.success('Dispatch Confirmed', {
        description: 'Generating BOL and notifying customer...'
      });
    }
  };

  // Get effective status
  const getShipmentStatus = (shipment) => {
    const shipmentId = shipment.id || shipment.dispatch_number;
    return shipmentStatuses[shipmentId] || shipment.status;
  };

  return (
    <div className="space-y-6" data-testid="wms-expedition">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("expedition", "Expedition")}</h1>
          <p className="text-muted-foreground mt-1">{t("scanverifiedLoadingAnd", "Scan-verified loading and carrier dispatch")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <FileText className="h-4 w-4" />{t("generateBol", "Generate BOL")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />{t("schedulePickup", "Schedule Pickup")}

          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowWorkflowDialog(true)}>
            <ScanLine className="h-4 w-4" />{t("startLoad", "Start Load")}

          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("stagedForPickup", "Staged for Pickup")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.staged}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("stillPacking", "Still Packing")}</p>
              <p className="text-2xl font-bold text-amber-600">{stats.packing}</p>
            </div>
            <Clock className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("shippedToday", "Shipped Today")}</p>
              <p className="text-2xl font-bold text-blue-600">{stats.shipped}</p>
            </div>
            <Truck className="h-8 w-8 text-blue-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Scan-Gated Dispatch Input */}
      <Card className="card-elevated bg-gradient-to-r from-indigo-50/50 to-transparent dark:from-indigo-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
              <ScanLine className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold">{t("scanverifiedLoading", "Scan-Verified Loading")}</h3>
              <p className="text-sm text-muted-foreground">{t("scanVehicleTo", "Scan vehicle to start loading, then scan each pallet/carton")}

              </p>
            </div>
            <Badge variant="outline" className="ml-auto gap-1">
              <Truck className="h-3 w-3" />{t("bolAutogenerate", "BOL Auto-Generate")}

            </Badge>
          </div>
          <BarcodeScanner
            onScan={handleQuickScan}
            scanMode="internal"
            placeholder="Scan vehicle ID or pallet/carton barcode..."
            showSamples />
          
        </CardContent>
      </Card>

      {/* Workflow Steps Guide */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <h4 className="font-medium mb-3">{t("scangatedDispatchProcess", "Scan-Gated Dispatch Process")}</h4>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</div>
              <span>{t("scanVehicle", "Scan Vehicle")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold">2</div>
              <span>{t("loadPallets", "Load Pallets")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-violet-500 text-white flex items-center justify-center text-xs font-bold">3</div>
              <span>{t("confirmBol", "Confirm BOL")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>{t("dispatched", "Dispatched")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduled Pickups */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-muted-foreground" />{t("scheduledPickupsToday", "Scheduled Pickups Today")}

          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            {scheduledPickups.map((pickup, index) =>
            <div
              key={index}
              className="p-4 rounded-lg border border-border bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/20">
              
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold">{pickup.carrier}</span>
                  </div>
                  <Badge variant="outline" className="font-mono">{pickup.time}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm mb-3">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {pickup.dock}
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Package className="h-4 w-4" />
                    {pickup.pallets}{t("pallets", "pallet(s)")}
                </div>
                </div>
                <Button
                size="sm"
                className="w-full gap-2"
                onClick={() => handleStartDispatch(pickup)}>
                
                  <ScanLine className="h-4 w-4" />{t("startLoading", "Start Loading")}

              </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Staged Shipments */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">{t("outboundShipments", "Outbound Shipments")}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search shipments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("orderId", "Order ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("palletId", "Pallet ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("destination", "Destination")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("carrier", "Carrier")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dock", "Dock")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("pickup", "Pickup")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ?
                <tr>
                    <td colSpan={8} className="py-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">{t("loadingShipments", "Loading shipments...")}</p>
                    </td>
                  </tr> :
                dispatches.length === 0 ?
                <tr>
                    <td colSpan={8} className="py-8 text-center">
                      <Truck className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-sm text-muted-foreground">{t("noShipmentsFound", "No shipments found")}</p>
                    </td>
                  </tr> :
                dispatches.map((shipment) => {
                  const shipmentId = shipment.id || shipment.dispatch_number;
                  const effectiveStatus = getShipmentStatus(shipment);
                  return (
                    <tr key={shipmentId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{shipment.orderId || shipment.order_id || '-'}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm">{shipment.palletId || shipment.pallet_id || '-'}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm">{shipment.destination || '-'}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm">{shipment.carrier || '-'}</span>
                      </td>
                      <td className="py-4 px-4">
                        {shipment.dock ?
                        <div className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-mono text-sm">{shipment.dock}</span>
                          </div> :

                        <span className="text-muted-foreground">-</span>
                        }
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm">
                          {shipment.scheduledPickup || shipment.scheduled_pickup ?
                          new Date(shipment.scheduledPickup || shipment.scheduled_pickup).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                          '-'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <StatusIndicator status={effectiveStatus} />
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Button
                          size="sm"
                          variant={effectiveStatus === 'shipped' || effectiveStatus === 'departed' ? 'outline' : 'default'}>
                          
                          {(effectiveStatus === 'staged' || effectiveStatus === 'scheduled') &&
                          <>
                              <ScanLine className="h-4 w-4 mr-1" />{t("load", "Load")}

                          </>
                          }
                          {(effectiveStatus === 'shipped' || effectiveStatus === 'departed') && 'View BOL'}
                          {effectiveStatus !== 'staged' && effectiveStatus !== 'shipped' && effectiveStatus !== 'scheduled' && effectiveStatus !== 'departed' && 'Verify'}
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Loading Dock Status */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("loadingDockStatus", "Loading Dock Status")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-4">
            {['DOCK-A1', 'DOCK-A2', 'DOCK-B1', 'DOCK-B2'].map((dock, index) => {
              const isOccupied = index === 0;
              return (
                <div
                  key={dock}
                  className={`p-4 rounded-lg border ${
                  isOccupied ?
                  'border-green-300 bg-green-50/50 dark:bg-green-950/20' :
                  'border-border bg-muted/20'}`
                  }>
                  
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono font-medium">{dock}</span>
                    {isOccupied ?
                    <Badge className="bg-green-100 text-green-700 border-green-200">{t("occupied", "Occupied")}</Badge> :

                    <Badge variant="outline">{t("available", "Available")}</Badge>
                    }
                  </div>
                  {isOccupied &&
                  <div className="text-sm text-muted-foreground">
                      <p>{t("fedexExpress", "FedEx Express")}</p>
                      <p className="text-xs">{t("pickup1400", "Pickup: 14:00")}</p>
                    </div>
                  }
                </div>);

            })}
          </div>
        </CardContent>
      </Card>

      {/* Scan Workflow Dialog */}
      <ScanWorkflowDialog
        open={showWorkflowDialog}
        onOpenChange={setShowWorkflowDialog}
        title={selectedVehicle ? `Loading: ${selectedVehicle.carrier}` : 'Dispatch Workflow'}
        icon={Truck}
        steps={dispatchWorkflowSteps}
        expectedItems={sampleDispatchItems.map((item) => ({ sku: item.id, gtin: item.id, quantity: 1 }))}
        onComplete={handleWorkflowComplete}
        onStepComplete={handleStepComplete}
        validationMode="flexible" />
      
    </div>);

};